import React, { useState, useEffect, useRef } from 'react';
import { useEntrance } from '../hooks/useEntrance';
import generatePDF from '../utils/generatePDF';

function safeString(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(safeString).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(' | ');
  }
  return String(value);
}

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

const ENTRANCE_OPTS = { threshold: 0.05 };

export default function CallReport({ report, onBack, onDownload, onDelete, onUpdate, token }) {
  const reportRef = useRef(null);
  useEntrance(reportRef, ENTRANCE_OPTS);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const { summary, transcript, suggestions, stats, talkRatio, customerIntel, researchItems } = report;

  const [starred, setStarred] = useState(report.starred || false);
  const [notes, setNotes] = useState(report.notes || '');
  const [notesSaved, setNotesSaved] = useState(true);
  const [notesTimer, setNotesTimer] = useState(null);

  useEffect(() => {
    setStarred(report.starred || false);
    setNotes(report.notes || '');
    setNotesSaved(true);
  }, [report.callSid]);

  const patchMeta = async (body) => {
    try {
      const res = await fetch(`${API_BASE}/api/reports/${report.callSid}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok && onUpdate) {
        onUpdate({ ...report, ...body });
      }
    } catch { /* ignore */ }
  };

  const handleStarToggle = () => {
    const next = !starred;
    setStarred(next);
    patchMeta({ starred: next });
  };

  const handleNotesChange = (value) => {
    setNotes(value);
    setNotesSaved(false);
    if (notesTimer) clearTimeout(notesTimer);
    const timer = setTimeout(() => {
      patchMeta({ notes: value });
      setNotesSaved(true);
    }, 800);
    setNotesTimer(timer);
  };

  return (
    <div className="call-report" ref={reportRef}>
      <div className="report-toolbar">
        <button className="report-back-btn" onClick={onBack}>Back to History</button>
        <div className="report-toolbar-right">
          <button
            className={`report-star-btn ${starred ? 'report-star-active' : ''}`}
            onClick={handleStarToggle}
            title={starred ? 'Unstar' : 'Star this report'}
          >
            {starred ? '★' : '☆'}
          </button>
          <button className="report-download-btn" onClick={onDownload}>Download JSON</button>
          <button className="report-download-btn report-pdf-btn" onClick={() => generatePDF(report)}>Download PDF</button>
          {onDelete && (
            <button className="report-delete-btn" onClick={() => { if (window.confirm('Delete this report?')) onDelete(); }}>
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="report-header">
        <h2 className="report-title">Call Report</h2>
        <div className="report-meta">
          <span>{new Date(report.startedAt).toLocaleString()}</span>
          <span>{report.duration ? Math.round(report.duration / 1000) + 's' : '—'}</span>
          <span>Playbook: {report.playbook}</span>
          {report.inputLanguage && report.inputLanguage !== 'en' && (
            <span>STT: {report.inputLanguage.toUpperCase()}</span>
          )}
          {report.outputLanguage && report.outputLanguage !== 'en' && (
            <span>Output: {report.outputLanguage.toUpperCase()}</span>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="report-section report-notes-section">
        <h3 className="report-section-title">
          Notes
          {!notesSaved && <span className="report-notes-saving">saving...</span>}
          {notesSaved && notes && <span className="report-notes-saved">✓</span>}
        </h3>
        <textarea
          className="report-notes-editor"
          placeholder="Add notes about this call..."
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          rows={3}
          maxLength={2000}
        />
      </div>

      {summary && (
        <div className="report-section">
          <h3 className="report-section-title">Summary</h3>

          {summary.error && (
            <div className="report-error-banner">
              Summary generation failed: {summary.error}
            </div>
          )}

          {summary.verdict && (
            <div className={`report-verdict ${summary.error ? 'verdict-error' : ''}`}>
              {summary.score != null && (
                <span className={`report-score ${summary.score >= 7 ? 'score-good' : summary.score >= 4 ? 'score-ok' : 'score-low'}`}>
                  {summary.score}/10
                </span>
              )}
              <span>{summary.verdict}</span>
            </div>
          )}

          {summary.went_well?.length > 0 && (
            <div className="report-list-block">
              <h4>What Went Well</h4>
              <ul>{summary.went_well.map((item, i) => <li key={i}>{safeString(item)}</li>)}</ul>
            </div>
          )}

          {summary.improvements?.length > 0 && (
            <div className="report-list-block">
              <h4>Areas for Improvement</h4>
              <ul>{summary.improvements.map((item, i) => <li key={i}>{safeString(item)}</li>)}</ul>
            </div>
          )}

          {summary.key_moments?.length > 0 && (
            <div className="report-list-block">
              <h4>Key Moments</h4>
              {summary.key_moments.map((m, i) => {
                const ts = safeString(m.timestamp_label || m.timestamp || m.time);
                const desc = safeString(m.description || m.text || m.moment);
                return (
                  <div key={i} className="report-moment">
                    {ts ? <><strong>{ts}:</strong> {desc}</> : desc}
                  </div>
                );
              })}
            </div>
          )}

          {summary.coaching_effectiveness && (
            <div className="report-list-block">
              <h4>Coaching Effectiveness</h4>
              <div className="report-field">
                <strong>Suggestions given:</strong> {summary.coaching_effectiveness.suggestions_given ?? '—'}
                {' / '}
                <strong>Accepted:</strong> {summary.coaching_effectiveness.suggestions_accepted ?? '—'}
              </div>
              {summary.coaching_effectiveness.assessment && (
                <div className="report-field">
                  {safeString(summary.coaching_effectiveness.assessment)}
                </div>
              )}
            </div>
          )}

          {summary.customer_sentiment && (
            <div className="report-field">
              <strong>Customer Sentiment:</strong> {safeString(summary.customer_sentiment)}
            </div>
          )}

          {summary.next_steps && (
            <div className="report-field">
              <strong>Next Steps:</strong> {safeString(summary.next_steps)}
            </div>
          )}
        </div>
      )}

      {stats && (
        <div className="report-section">
          <h3 className="report-section-title">Stats</h3>
          <div className="report-stats-grid">
            <div className="report-stat">
              <div className="report-stat-value">{stats.transcriptCount}</div>
              <div className="report-stat-label">Transcript Lines</div>
            </div>
            <div className="report-stat">
              <div className="report-stat-value">{stats.suggestionCount}</div>
              <div className="report-stat-label">Suggestions</div>
            </div>
            <div className="report-stat">
              <div className="report-stat-value">{stats.suggestionsAccepted}</div>
              <div className="report-stat-label">Accepted</div>
            </div>
            <div className="report-stat">
              <div className="report-stat-value">
                {talkRatio ? `${Math.round(talkRatio.ratio * 100)}%` : '—'}
              </div>
              <div className="report-stat-label">Agent Talk %</div>
            </div>
          </div>
        </div>
      )}

      {report.platformCost && (
        <div className="report-section">
          <h3 className="report-section-title">Platform Fee</h3>
          <div className="report-stats-grid">
            <div className="report-stat">
              <div className="report-stat-value">
                {Math.floor(report.platformCost.durationSeconds / 60)}m {report.platformCost.durationSeconds % 60}s
              </div>
              <div className="report-stat-label">Duration</div>
            </div>
            <div className="report-stat">
              <div className="report-stat-value">
                ${report.platformCost.ratePerMinute?.toFixed(2)}/min
              </div>
              <div className="report-stat-label">Rate</div>
            </div>
            <div className="report-stat">
              <div className="report-stat-value">${report.platformCost.dollars}</div>
              <div className="report-stat-label">Fee Charged</div>
            </div>
            {report.platformCost.remainingBalance != null && (
              <div className="report-stat">
                <div className="report-stat-value">${(report.platformCost.remainingBalance / 100).toFixed(2)}</div>
                <div className="report-stat-label">Balance Left</div>
              </div>
            )}
          </div>
        </div>
      )}

      {customerIntel?.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">Customer Intelligence</h3>
          <div className="intel-list">
            {customerIntel.map((item, i) => {
              const badges = {
                info: 'intel-badge-info',
                concern: 'intel-badge-concern',
                request: 'intel-badge-request',
                objection: 'intel-badge-objection',
              };
              return (
                <div key={i} className="intel-item">
                  <span className={`intel-badge ${badges[item.type] || badges.info}`}>
                    {(item.type || 'info').toUpperCase()}
                  </span>
                  <span className="intel-text">{safeString(item.text)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {researchItems?.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">Research Notes</h3>
          <div className="research-list" style={{ maxHeight: 'none' }}>
            {researchItems.map((item, i) => {
              const badges = {
                company: 'research-badge-company',
                product: 'research-badge-product',
                regulation: 'research-badge-regulation',
                term: 'research-badge-term',
                person: 'research-badge-person',
                topic: 'research-badge-topic',
              };
              return (
                <div key={i} className="research-card">
                  <div className="research-card-header">
                    <span className={`research-badge ${badges[item.type] || badges.topic}`}>
                      {(item.type || 'topic').toUpperCase()}
                    </span>
                    <span className="research-subject">{item.subject}</span>
                    {item.webEnriched && <span className="research-web-tag">WEB</span>}
                  </div>
                  <div className="research-insight">{item.insight}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {suggestions?.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">Coaching Suggestions</h3>
          <div className="report-suggestions">
            {suggestions.map((s, i) => (
              <div key={i} className={`report-suggestion report-suggestion-${s.urgency}`}>
                <div className="report-suggestion-header">
                  <span className="report-suggestion-urgency">{s.urgency?.toUpperCase()}</span>
                  {s.action && <span className={`report-suggestion-action action-${s.action}`}>{s.action}</span>}
                </div>
                <div className="report-suggestion-text">{s.text}</div>
                {s.reason && <div className="report-suggestion-reason">{s.reason}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {report.transcriptHidden ? (
        <div className="report-section">
          <h3 className="report-section-title">Full Transcript</h3>
          <div className="report-transcript-hidden">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Transcript not saved — Compliance Mode was enabled</span>
          </div>
        </div>
      ) : transcript?.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title">Full Transcript</h3>
          <div className="report-transcript">
            {transcript.map((entry, i) => {
              const sm = report.speakerMap || {};
              let label = entry.role === 'agent' ? 'Agent' : 'Customer';
              if (entry.role !== 'agent' && entry.speakerIndex != null) {
                label = sm[entry.speakerIndex] || entry.speakerLabel || `Speaker ${entry.speakerIndex}`;
              }
              return (
                <div key={i} className={`report-transcript-line role-${entry.role}`}>
                  <span className="report-transcript-role">
                    {label}
                  </span>
                  <span className="report-transcript-time">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="report-transcript-text">{entry.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
